import { Dispatch } from "react";
import { Action } from "redux";
import API from "../../services/api";
import { Product, ProductFields, ContentStatus, ProductUpdate, ProductFilter } from "typings";
import { createStatusMessage } from "../ui/StatusMessage";
import { statusAdd } from "../ui/action";

export interface DeleteProductsAction extends Action {
    type: "DELETE_PRODUCTS";
}

export const productRemoveSuccess = (): DeleteProductsAction => ({
    type: "DELETE_PRODUCTS"
});

export interface LoadProductsAction extends Action
{
    type: "LOAD_PRODUCTS";
}

export interface ProductsLoadSuccessAction
{
    type: "PRODUCTS_LOAD_SUCCESS";
    products: Product[];
    pages: number;
}

export interface GetProductsSuccessAction
{
    type: "GET_PRODUCTS_SUCCESS";
    products: Product[];
   
}

export interface ProductLoadSuccessAction
{
    type: "PRODUCT_LOAD_SUCCESS";
    product: Product;
}

export const productLoadSuccess = (product: Product): ProductLoadSuccessAction => ({
    type: "PRODUCT_LOAD_SUCCESS",
    product,
});

export const productsLoadSuccess = (products: Product[], pages: number): ProductsLoadSuccessAction => ({
    type: "PRODUCTS_LOAD_SUCCESS",
    products,
    pages
});

export interface LoadProductsFailureAction
{
    type: "LOAD_PRODUCTS_FAILURE";
    error: number;
}

export const loadProductsFailure = (error: number): LoadProductsFailureAction => ({
    type: "LOAD_PRODUCTS_FAILURE",
    error
});


export interface ProductUpdateFailureAction
{
    type: "PRODUCT_UPDATE_FAILURE";
    id: string;
    values: ProductFields;
}

export const productUpdateFailure = (id: string, values: ProductFields): ProductUpdateFailureAction => ({
    type: "PRODUCT_UPDATE_FAILURE",
    id, values
});

export interface ProductUpdateAction
{
    type: "PRODUCT_UPDATE";
    id: string;
    data: ProductFields;
}

export interface ProductUpdateMultipleAction
{
    type: "PRODUCT_UPDATE_MULTIPLE";
    products: Product[];
    
}

export interface ChangeProductStatusAction extends Action
{
    type: "CHANGE_PRODUCT_STATUS";
    productId: string;
    status: ContentStatus;
}


export const productsUpdateMultiple = (products: Product[]): ProductUpdateMultipleAction =>
    ({ products, type: "PRODUCT_UPDATE_MULTIPLE" });


export const changeProductStatus = (productId: string, status: ContentStatus): ChangeProductStatusAction => 
    ({ type: "CHANGE_PRODUCT_STATUS", productId, status });



export const productUpdate_ = (id: string, data: ProductFields): ProductUpdateAction => ({
    type: "PRODUCT_UPDATE",
    id, data
});

export const updateProductData = (id: string, data:  {[key: string]: any}) => async (dispatch: Dispatch<Action>) =>
{   
  
    dispatch(productUpdate_(id, data));
    try
    {
        const product = await API.product.updateData(id, data);
        dispatch(productLoadSuccess(product));
    } 
    catch (e)
    {
        dispatch(productUpdateFailure(id, data));
    }
};

export const updateProduct = (productId: string, update: ProductUpdate) => async (dispatch: Dispatch<Action>) =>
{   
    try
    {
        const product = await API.product.update(productId, update);
        dispatch(productLoadSuccess(product));
    } 
    catch (e)
    {
        
        const statusFinished = createStatusMessage("Save data failed", "error", "product");
        dispatch(statusAdd(statusFinished));
    }
};

export const deleteProduct = (productId: string) => async (dispatch: Dispatch<Action>) =>
{
    try
    {
        await API.product.delete(productId);
        dispatch(productRemoveSuccess());
    }
    catch (e)
    {

        const statusFinished = createStatusMessage("Delete product failed", "error", "product");
        dispatch(statusAdd(statusFinished));
    }
};

export const updateMultipleProducts = (productIds: string[], update: ProductUpdate) => async (dispatch: Dispatch<Action>) =>
{   
    try
    {
        const products = await API.product.updateMultiple(productIds, update);
        dispatch(productsUpdateMultiple(products));
      
    } 
    catch (e)
    {
        
        const statusFinished = createStatusMessage("Updating products failed", "error", "product");
        dispatch(statusAdd(statusFinished));
    }
};



export const createMainProduct = (children: Product[], name: string, templateId: string, navigateToProduct: (id: string) => void) => 
    async (dispatch: Dispatch<Action>) =>
{
    const childrenIds = children.map(x => x.id);
    const products = await API.product.createMainProduct(childrenIds, name, templateId);
 
    const mainProduct = products.find(x => childrenIds.indexOf(x.id) === -1);
    if (mainProduct)
    {
        navigateToProduct(mainProduct.id);
    }
};

export const addToProduct = (parentId: string, children: Product[]) => async (dispatch: Dispatch<Action>) =>
{

    await API.product.updateParent(parentId, children.map(x => x.id).filter(x => x !== parentId) );
};

export const clearField = (productId: string, fieldName: string)  => async (dispatch: Dispatch<Action>) =>
{
    const product = await API.product.clearProductField(productId, fieldName);
    dispatch(productLoadSuccess(product));
};

export const loadProduct = (id: string) => async (dispatch: Dispatch<Action>) =>
{
    try
    {
        const product = await API.product.fetchOne(id);
        dispatch(productLoadSuccess(product));
      
        let current = product;
        while (current.parentId)
        {
            current = await API.product.fetchOne(current.parentId);
            if (current)
            {
                dispatch(productLoadSuccess(current));
            }
        }
    } 
    catch
    {
        const error = createStatusMessage("Product not found", "error", "product");
        dispatch(statusAdd(error));
    }
};
export const getProducts = (products: string[]) => async (dispatch: Dispatch<Action>) =>
{
    try
    {
        const result = await API.product.getProducts(products);
        dispatch( {products: result, type:"GET_PRODUCTS_SUCCESS" } as GetProductsSuccessAction);
    } 
    catch (e)
    {
        const statusFinished = createStatusMessage("Search Failed", "error", "product");
        dispatch(statusAdd(statusFinished));
    }
};

export const loadProducts = (filter: ProductFilter) => async (dispatch: Dispatch<Action>) =>
{
    try
    {
        const result = await API.product.fetchProducts(filter);
        dispatch(productsLoadSuccess(result.products, result.pages));
    } 
    catch
    {
        const statusFinished = createStatusMessage("Search Failed", "error", "product");
        dispatch(statusAdd(statusFinished));
    }
};


export type ProductActions = 
      LoadProductsAction
    | ProductsLoadSuccessAction
    | LoadProductsFailureAction
    | ProductUpdateMultipleAction 
    | ProductUpdateFailureAction
    | ProductUpdateAction
    | ChangeProductStatusAction
    | ProductLoadSuccessAction
    | GetProductsSuccessAction;

export type ProductFailureActions = LoadProductsFailureAction | ProductUpdateFailureAction;


// WEBPACK FOOTER //
// ./src/state/products/action.ts